import { inject, Injectable, signal } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { UserBackend } from "src/app/backend/api/user.backend";
import { PRODUCT_TOKEN } from "src/app/core/product.token";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { getCustomerNominative } from "src/app/support/customer";
import {
  getCurrentSoftwareProductVersion,
  SoftwareProduct
} from "src/app/support/product";
import { getPublicFileSrcById } from "src/app/support/public-file";

declare global {
  interface Window {
    $chatwoot: {
      setUser: (userId: string, user: any) => void;
      setCustomAttributes: (attributes: any) => void;
      toggle: (action: "open" | "close") => void;
      setConversationCustomAttributes: (attributes: any) => void;
    };
    chatwootSettings: {
      hideMessageBubble: boolean;
      showUnreadMessagesDialog: boolean;
      position: "left" | "right";
      useBrowserLanguage: boolean;
      locale: string;
      type: "standard" | "expanded_bubble";
      darkMode: "light" | "dark" | "auto";
      baseDomain?: string;
    };
    chatwootSDK: {
      run: (config: any) => void;
    };
  }
}

@Injectable()
export class CustomerSupportService {
  enabled = signal<boolean>(false);

  isSupported = signal<boolean>(false);

  readonly #bootstrapService = inject(BootstrapService);
  readonly #product = inject(PRODUCT_TOKEN);
  readonly #userBackend = inject(UserBackend);
  readonly #regionService = inject(RegionService);

  constructor() {
    switch (this.#product) {
      case SoftwareProduct.PMS:
      case SoftwareProduct.ROCKET: {
        this.isSupported.set(true);
        break;
      }
      default: {
        this.isSupported.set(false);
        break;
      }
    }
  }

  async enable(): Promise<void> {
    const BASE_URL = "https://chatwoot.snoots.pet";
    const scriptElement = document.createElement("script");
    const firstScriptElement = document.getElementsByTagName("script")[0];

    scriptElement.src = BASE_URL + "/packs/js/sdk.js";
    scriptElement.async = true;
    scriptElement.onload = () => {
      window.addEventListener("chatwoot:ready", () => {
        if (!user) {
          return;
        }

        window.$chatwoot.setUser(user.id, {
          name: getCustomerNominative(user),
          avatar_url: getPublicFileSrcById(user.avatar, { useFallback: true }),
          email: user.email,
          phone_number: user.phone,
          company_name: tenant?.name
        });

        window.$chatwoot.setConversationCustomAttributes({
          slug: account.slug,
          account: account!.id,
          tenant_name: tenant?.name,
          tenant_id: tenant?.id,
          product: this.#product,
          version: getCurrentSoftwareProductVersion(),
          region: this.#regionService.get(),
          locale: this.#regionService.getLocale(),
          license: tenant?.plan.id
        });

        this.enabled.set(true);
      });

      const isDark = this.#bootstrapService.getCurrentTheme() === "dark";

      window.chatwootSettings = {
        hideMessageBubble: true,
        showUnreadMessagesDialog: true, // Disable the unread message dialog
        position: "right", // This can be left or right
        useBrowserLanguage: true, // Set widget language from user's browser
        locale: "en", // Language to be set
        type: "standard", // [standard, expanded_bubble]
        darkMode: isDark ? "dark" : "light" // [light, auto]
        // baseDomain: "yourdomain.com" // configure if you want to track users across subdomains
      };

      window.chatwootSDK.run({
        websiteToken: "hvq9VB2qGmt4oxnLQ7U2cfn9",
        baseUrl: BASE_URL
      });
    };

    firstScriptElement?.parentNode?.insertBefore(
      scriptElement,
      firstScriptElement
    );

    const user = this.#bootstrapService.getCurrentUser();
    const tenant = this.#bootstrapService.getCurrentTenant();
    const account = await lastValueFrom(
      this.#userBackend.loadAccount(tenant!.account!)
    );

    return;
  }

  show(): void {
    (window as any).$chatwoot.toggle("open");
  }
}
